import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutsideClickEffect } from '@saviynt/common';
import {
  Box,
  ButtonCore,
  Icon,
  InlineMessage,
  InputField,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import useDebounceForFunctions from '../../../hooks/use-debounceforfunction';
import { LIMIT_CHARACTERS_FOR_QUERY } from '../../../page/CertificationList/CertsListBody/constant';
import {
  getCertDetailsData,
  selectAllBulkAction,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setCertTriggerFromSearchOrFilter,
  setCheckForBulkAction,
  setIndeterminateForBulkAction,
  setSidePanelVisible,
  setTriggerClearBtnForSearch,
} from '../../../redux/certdetails/action';
import { ITEMS_API_OFFSET } from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';

import { DELAY_FOR_ENTER_PRESS } from './constant';
import { invertedMessages } from './messages';

import './CertsSearchBar.css';

function CertsSearchBar({
  isMoreOpen,
  isSearchBarExpanded,
  setIsSearchBarExpanded,
  isSearchAlert,
  setIsSearchAlert,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  // Used to delay the labels and buttons from showing up to fast
  const [isTimeOutForExpanded, setIsTimeOutForExpanded] = useState(false);
  const searchBarButtonRef = useRef(null);
  const searchBarInputRef = useRef(null);
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);

  const {
    bulkAction: {
      isChecked: isBulkChecked,
      isIndeterminate: isBulkIndeterminate,
    },
    core: { columnNames, isLoading },
    global: { certKey },
    tabs: { tabData },
    viewControls: { triggerClearBtnForSearch },
  } = useSelector((state) => state?.certifications?.details);

  const activeTabCount = tabData?.find((tab) => tab.isActive)?.count;

  const searchBarClasses = classnames(
    'CertsSearchBar',
    isTimeOutForExpanded && 'CertsSearchBar--isExpanded',
    (activeTabCount === 0 || isLoading) && 'CertsSearchBar--isDisabled'
  );

  const searchBarButtonClasses = classnames(
    'CertsSearchBar-button',
    activeTabCount === 0 || (isLoading && 'CertsSearchBar-button--isDisabled')
  );

  useEffect(() => {
    if (searchQuery) {
      setIsSearchBarExpanded(true);
      setIsTimeOutForExpanded(true);
    }
  }, [searchQuery]);

  const handleSearchExpand = () => {
    // Disallow collapse when value is present

    if (searchQuery) return;
    setIsSearchAlert(false);

    const inputField = searchBarInputRef?.current;

    if (isSearchBarExpanded) {
      setIsTimeOutForExpanded(false);
      inputField.blur();

      setTimeout(() => {
        setIsSearchBarExpanded(false);
      }, 200);
    } else {
      inputField?.focus();
      setIsSearchBarExpanded(true);
      setIsTimeOutForExpanded(true);
    }
  };

  useOutsideClickEffect(
    searchBarInputRef,
    () => {
      handleSearchExpand();
    },
    isTimeOutForExpanded && !isMoreOpen
  );

  // TODO: Give a timeframe for 1 min if Enter key is pressed for the same search query
  const handleSearch = useDebounceForFunctions(() => {
    const isSearchTextValid = Boolean(
      searchQuery.trim().length === 0 ||
        searchQuery.trim().length > LIMIT_CHARACTERS_FOR_QUERY
    );

    if (!isSearchTextValid) {
      setIsSearchAlert(true);

      return;
    }

    if (isBulkChecked || isBulkIndeterminate) {
      dispatch(setBulkActionBySelectAll(true));

      dispatch(selectAllBulkAction(certKey, true))
        .then(() => {
          dispatch(setCheckForBulkAction(false));
          dispatch(setIndeterminateForBulkAction(false));
        })
        .catch((error) => {
          dispatch(setBulkActionAlert(true, error.message));
        });
    }

    if (certKey) {
      dispatch(setCertTriggerFromSearchOrFilter(true));
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          ITEMS_API_OFFSET,
          0,
          searchQuery.trim()
        )
      );
      dispatch(setSidePanelVisible(false));
    }
  }, DELAY_FOR_ENTER_PRESS);

  const handleChange = (event) => {
    const isSearchTextValid = Boolean(
      event.target.value.trim().length === 0 ||
        event.target.value.trim().length > LIMIT_CHARACTERS_FOR_QUERY
    );

    setSearchQuery(event.target.value);

    if (isSearchAlert && isSearchTextValid) {
      setIsSearchAlert(false);
    }
  };

  const resetSearch = () => {
    dispatch(setCertTriggerFromSearchOrFilter(true));
    dispatch(
      getCertDetailsData(certKey, columnNames, ITEMS_API_OFFSET, 0)
    ).then(() => {
      setSearchQuery('');
    });
    dispatch(setTriggerClearBtnForSearch(false));
    handleSearchExpand();
  };

  const handleSearchBoxClose = () => {
    if (searchQuery.trim().length !== 0) {
      dispatch(setSidePanelVisible(false));
      setIsSearchAlert(false);
      resetSearch();

      return;
    }

    handleSearchExpand();
  };

  useEffect(() => {
    if (triggerClearBtnForSearch) resetSearch();
  }, [triggerClearBtnForSearch]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') handleSearch();
  };

  return (
    <Box tag='div' className={searchBarClasses}>
      <InputField
        ref={searchBarInputRef}
        name='CertsSearchBar'
        placeholder={l('Search by certification name or username')}
        kind='search'
        size='medium'
        prefixIcon={
          <Icon kind='Search' color='--color-foreground-neutral-subtle' />
        }
        value={searchQuery}
        setValue={setSearchQuery}
        onChange={handleChange}
        resetValue={handleSearchBoxClose}
        onKeyDown={handleKeyDown}
        isDisabled={activeTabCount === 0 || isLoading}
        tabIndex={isSearchBarExpanded ? 0 : -1}
      />
      <ButtonCore
        ref={searchBarButtonRef}
        className={searchBarButtonClasses}
        onClick={handleSearchExpand}
        tabIndex={!isSearchBarExpanded ? 0 : -1}
        isDisabled={activeTabCount === 0 || isLoading}
      />
      {isSearchAlert && isSearchBarExpanded && (
        <InlineMessage
          text={l('Type more than 2 characters to see results.')}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      )}
      <span className='CertsSearchBar-overlay' />
    </Box>
  );
}

CertsSearchBar.propTypes = {
  isMoreOpen: PropTypes.bool,
  isSearchBarExpanded: PropTypes.bool.isRequired,
  setIsSearchBarExpanded: PropTypes.func.isRequired,
  isSearchAlert: PropTypes.bool.isRequired,
  setIsSearchAlert: PropTypes.func.isRequired,
};

CertsSearchBar.defaultProps = { isMoreOpen: false };

export default CertsSearchBar;
